import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Phone, Mail } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { SITE } from "../components/Footer";

import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INSURANCE_TYPES = [
  { value: "auto", label: "Auto Insurance" },
  { value: "home", label: "Home Insurance" },
  { value: "commercial", label: "Commercial Insurance" },
  { value: "life", label: "Life Insurance" },
  { value: "other", label: "Something else" },
];

export default function Quote() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    insurance_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.phone.trim() || form.phone.trim().length < 5)
      e.phone = "Please enter a valid phone";
    if (!form.insurance_type) e.insurance_type = "Please select a type";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Invalid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        insurance_type: form.insurance_type,
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.message.trim()) payload.message = form.message.trim();

      await axios.post(`${API}/quote`, payload);
      setSubmitted(true);
      toast.success("Quote request received", {
        description: "An advisor will reach out shortly.",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong", {
        description:
          err?.response?.data?.detail || "Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-quote" className="bg-white min-h-screen">
      <Navbar />
      <main className="pt-24 md:pt-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
          <Link
            to="/"
            data-testid="quote-back-link"
            className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-black"
          >
            <ArrowLeft size={14} /> Back
          </Link>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left copy */}
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.22em] text-neutral-500 mb-6">
                Get a quote
              </div>
              <h1
                data-testid="quote-heading"
                className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.04em] text-black leading-[0.95]"
              >
                Tell us about
                <br />
                <span className="italic font-medium">your world.</span>
              </h1>
              <p className="mt-6 text-neutral-600 leading-relaxed max-w-md">
                A real advisor — not a call center — will reach out within
                one business day with a clean, tailored recommendation.
              </p>

              <div className="mt-12 space-y-5 border-t border-neutral-200 pt-8">
                <div className="flex items-center gap-3 text-[15px]">
                  <Phone size={16} className="text-neutral-500" />
                  <a href={SITE.phoneHref} className="text-black hover:underline">
                    {SITE.phone}
                  </a>
                </div>
                <div className="flex items-center gap-3 text-[15px]">
                  <Mail size={16} className="text-neutral-500" />
                  <a
                    href={`mailto:${SITE.email}`}
                    className="text-black hover:underline"
                  >
                    {SITE.email}
                  </a>
                </div>
              </div>

              <div className="mt-12 p-6 border border-neutral-200 rounded-2xl bg-[#fafafa]">
                <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-3">
                  What to expect
                </div>
                <ul className="space-y-2 text-sm text-neutral-700 leading-relaxed">
                  <li>— A short, sharp intake call (15 min or less)</li>
                  <li>— Comparison across 40+ top carriers</li>
                  <li>— One clear recommendation, no pressure</li>
                </ul>
              </div>
            </div>

            {/* Right form */}
            <div className="lg:col-span-7">
              <div className="bg-white border border-neutral-200 rounded-3xl p-8 md:p-12">
                {submitted ? (
                  <div
                    data-testid="quote-success"
                    className="flex flex-col items-start gap-6 py-10"
                  >
                    <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center">
                      <CheckCircle2 size={22} />
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-[-0.02em] text-black">
                      Request received.
                    </h2>
                    <p className="text-neutral-600 max-w-md leading-relaxed">
                      Thank you, {form.name.split(" ")[0] || "there"}. An
                      advisor will reach out shortly at the number you provided.
                    </p>
                    <Link
                      to="/"
                      data-testid="quote-home-link"
                      className="mt-2 inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-full text-sm font-medium"
                    >
                      Back to home
                    </Link>
                  </div>
                ) : (
                  <form
                    onSubmit={onSubmit}
                    data-testid="quote-form"
                    className="flex flex-col gap-6"
                    noValidate
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="name" className="text-xs uppercase tracking-[0.14em] text-neutral-500">
                          Full name *
                        </Label>
                        <Input
                          id="name"
                          data-testid="quote-input-name"
                          value={form.name}
                          onChange={(e) => setField("name", e.target.value)}
                          placeholder="Jane Doe"
                          className="h-12 rounded-lg border-neutral-300 focus-visible:ring-black"
                        />
                        {errors.name && (
                          <span className="text-xs text-red-600">{errors.name}</span>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label htmlFor="phone" className="text-xs uppercase tracking-[0.14em] text-neutral-500">
                          Phone *
                        </Label>
                        <Input
                          id="phone"
                          data-testid="quote-input-phone"
                          value={form.phone}
                          onChange={(e) => setField("phone", e.target.value)}
                          placeholder="(555) 123-4567"
                          inputMode="tel"
                          className="h-12 rounded-lg border-neutral-300 focus-visible:ring-black"
                        />
                        {errors.phone && (
                          <span className="text-xs text-red-600">{errors.phone}</span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="email" className="text-xs uppercase tracking-[0.14em] text-neutral-500">
                          Email
                        </Label>
                        <Input
                          id="email"
                          data-testid="quote-input-email"
                          type="email"
                          value={form.email}
                          onChange={(e) => setField("email", e.target.value)}
                          placeholder="jane@company.com"
                          className="h-12 rounded-lg border-neutral-300 focus-visible:ring-black"
                        />
                        {errors.email && (
                          <span className="text-xs text-red-600">{errors.email}</span>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label className="text-xs uppercase tracking-[0.14em] text-neutral-500">
                          Type of insurance *
                        </Label>
                        <Select
                          value={form.insurance_type}
                          onValueChange={(v) => setField("insurance_type", v)}
                        >
                          <SelectTrigger
                            data-testid="quote-select-type"
                            className="h-12 rounded-lg border-neutral-300 focus:ring-black"
                          >
                            <SelectValue placeholder="Select coverage" />
                          </SelectTrigger>
                          <SelectContent>
                            {INSURANCE_TYPES.map((t) => (
                              <SelectItem
                                key={t.value}
                                value={t.value}
                                data-testid={`quote-select-option-${t.value}`}
                              >
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.insurance_type && (
                          <span className="text-xs text-red-600">
                            {errors.insurance_type}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Label htmlFor="message" className="text-xs uppercase tracking-[0.14em] text-neutral-500">
                        Anything we should know?
                      </Label>
                      <Textarea
                        id="message"
                        data-testid="quote-input-message"
                        value={form.message}
                        onChange={(e) => setField("message", e.target.value)}
                        placeholder="Share any specifics about what you're looking to cover..."
                        rows={4}
                        className="rounded-lg border-neutral-300 focus-visible:ring-black"
                      />
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-2">
                      <p className="text-xs text-neutral-500 max-w-xs leading-relaxed">
                        By submitting, you agree to be contacted about your
                        insurance needs. No spam, no pressure.
                      </p>
                      <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="quote-submit-btn"
                        className="h-12 px-8 rounded-full bg-black hover:bg-neutral-800 text-white text-sm font-medium tracking-wide"
                      >
                        {submitting ? "Sending..." : "Submit Request →"}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
